// ManualUpload.js
import React, { useState, useContext } from "react";
import CreatableSelect from "react-select/creatable";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import colors from "./Colors";

import api from "../api/index";
import { UserContext } from "../UserContext";
import { Modal } from "antd";
import { useNavigate } from "react-router-dom";
import "./styles/ManualUpload.css";
import { motion } from "framer-motion";

const initialHours = {
  Sat: { closed: false, open: "--:--", close: "--:--" },
  Sun: { closed: false, open: "--:--", close: "--:--" },
  Mon: { closed: false, open: "--:--", close: "--:--" },
  Tues: { closed: false, open: "--:--", close: "--:--" },
  Wed: { closed: false, open: "--:--", close: "--:--" },
  Thur: { closed: false, open: "--:--", close: "--:--" },
  Fri: { closed: false, open: "--:--", close: "--:--" },
};

// these are the ones we validate
const REQUIRED_FIELDS = [
  "business_name",
  "business_address",
  "city",
  "state",
  "zipcode",
  "contact_name",
  "contact_email",
  "contact_phone",
];

const daysOrder = [
  "Sun",
  "Mon",
  "Tues",
  "Wed",
  "Thur",
  "Fri",
  "Sat",
];

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr || timeStr === "--:--") return null;
  const [h, m] = timeStr.split(":").map((v) => parseInt(v, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const ManualUpload = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  // core form + hours
  const [manualForm, setManualForm] = useState({});
  const [hours, setHours] = useState(initialHours);

  // hours validation state
  const [hoursErrorDays, setHoursErrorDays] = useState([]);
  const [hoursErrorMessage, setHoursErrorMessage] = useState("");

  // required-field validation state
  const [missingFields, setMissingFields] = useState([]);
  const [toastMessage, setToastMessage] = useState("");

  // fee-related state (self-contained, same pattern as LocationDetails)
  const vatmPercentage = user?.vatm_percentage ?? 0;
  const [customerTotalFee, setCustomerTotalFee] = useState("");
  const [retailLocationFee, setRetailLocationFee] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  // --------- input handlers ----------

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setManualForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleHoursChange = (day, field, value) => {
    setHours((prev) => {
      const prevDay =
        prev[day] || { closed: false, open: "--:--", close: "--:--" };

      // Toggle closed: when closing, reset times to sentinel; when opening, keep existing times
      if (field === "closed") {
        if (value) {
          return {
            ...prev,
            [day]: { closed: true, open: "--:--", close: "--:--" },
          };
        } else {
          return {
            ...prev,
            [day]: { ...prevDay, closed: false },
          };
        }
      }

      // open / close time update
      return {
        ...prev,
        [day]: {
          ...prevDay,
          [field]: value || "--:--",
        },
      };
    });
  };

  // Optional helper: copy Monday's hours to all days (keeps Closed days closed)
  const applyDayToAll = (sourceDay) => {
    setHours((prev) => {
      const source = prev[sourceDay];
      const hasTimes =
        source &&
        !source.closed &&
        source.open &&
        source.open !== "--:--" &&
        source.close &&
        source.close !== "--:--";
      if (!hasTimes) {
        return prev;
      }

      const updated = { ...prev };
      Object.keys(prev).forEach((day) => {
        const current =
          prev[day] || { closed: false, open: "--:--", close: "--:--" };
        const isClosed = !!current.closed;
        updated[day] = {
          ...current,
          open: isClosed ? "--:--" : source.open,
          close: isClosed ? "--:--" : source.close,
        };
      });
      return updated;
    });
  };

  // --------- fee helpers (mirroring LocationDetails logic) ----------

  const calculateSparesFee = () => {
    const fee = parseFloat(customerTotalFee) || 0;
    return fee * (vatmPercentage / 100);
  };

  const calculateYourNetFee = () => {
    const fee = parseFloat(customerTotalFee) || 0;
    const retail = parseFloat(retailLocationFee) || 0;
    const spares = calculateSparesFee();
    return fee - retail - spares;
  };

  const handleFeeInputChange = (field, value) => {
    const numericValue = value.replace(/[^0-9.]/g, "");

    if (field === "customerTotalFee") {
      setCustomerTotalFee(numericValue);
      setRetailLocationFee("");
      setShowErrorMessage(false);
    }
  };

  const getMaxAllowedRetailFee = () => {
    const fee = parseFloat(customerTotalFee) || 0;
    const spares = fee * (vatmPercentage / 100);
    return fee - spares;
  };

  const handleRetailLocationFeeChange = (value) => {
    const fee = parseFloat(customerTotalFee) || 0;
    if (fee <= 0) return;

    let numericValue = value.replace(/[^0-9.]/g, "");

    if (numericValue === "" || numericValue === ".") {
      setRetailLocationFee(numericValue);
      setShowErrorMessage(false);
      return;
    }

    const inputValue = parseFloat(numericValue);
    const maxAllowed = getMaxAllowedRetailFee();

    if (!isNaN(inputValue) && inputValue <= maxAllowed) {
      setRetailLocationFee(numericValue);
      setShowErrorMessage(false);
    } else if (!isNaN(inputValue) && inputValue > maxAllowed) {
      setShowErrorMessage(true);
      setTimeout(() => setShowErrorMessage(false), 3000);
    }
  };

  const handleRetailLocationFocus = () => {
    const fee = parseFloat(customerTotalFee) || 0;
    if (fee <= 0) setShowTooltip(true);
  };

  const handleRetailLocationBlur = () => {
    setShowTooltip(false);
  };

  // --------- validation helpers ----------

  const validateHours = () => {
    const missing = [];
    const reversed = [];

    daysOrder.forEach((day) => {
      const dayData = hours[day] || {};
      const isClosed = !!dayData.closed;
      const open = dayData.open && dayData.open !== "--:--" ? dayData.open : "";
      const close =
        dayData.close && dayData.close !== "--:--" ? dayData.close : "";

      // Required rule: each day must EITHER be closed, OR have both open & close
      if (isClosed) {
        return;
      }

      if (!open || !close) {
        missing.push(day);
        return;
      }

      const openMinutes = parseTimeToMinutes(open);
      const closeMinutes = parseTimeToMinutes(close);

      if (
        openMinutes === null ||
        closeMinutes === null ||
        openMinutes >= closeMinutes
      ) {
        reversed.push(day);
      }
    });

    const combinedErrors = [...new Set([...missing, ...reversed])];

    if (combinedErrors.length > 0) {
      setHoursErrorDays(combinedErrors);

      const parts = [];
      if (missing.length > 0) {
        parts.push(
          `Either mark these days as Closed or enter BOTH opening and closing times: ${missing.join(
            ", "
          )}.`
        );
      }
      if (reversed.length > 0) {
        parts.push(
          `Opening time must be earlier than closing time for: ${reversed.join(
            ", "
          )}.`
        );
      }

      setHoursErrorMessage(parts.join(" "));
      return false;
    }

    setHoursErrorDays([]);
    setHoursErrorMessage("");
    return true;
  };

  const validateRequiredFields = () => {
    const missing = REQUIRED_FIELDS.filter((field) => {
      const value = (manualForm[field] || "").toString().trim();
      return !value;
    });

    setMissingFields(missing);

    if (missing.length === 0) {
      setToastMessage("");
      return true;
    }

    // toast instead of modal for many missing fields
    if (missing.length > 3) {
      const msg = `You have ${missing.length} required fields missing. Please complete them.`;
      setToastMessage(msg);
      setTimeout(() => setToastMessage(""), 4000);
    } else {
      setToastMessage("");
    }

    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1) First check required fields
    const fieldsOk = validateRequiredFields();
    // 2) then hours
    const hoursOk = validateHours();

    // stop if either failed
    if (!fieldsOk || !hoursOk) return;

    // clear any leftovers
    setMissingFields([]);
    setToastMessage("");
    setHoursErrorDays([]);
    setHoursErrorMessage("");

    if (!user) {
      Modal.error({
        title: "Not signed in",
        content: "Please sign in again before adding a location.",
      });
      return;
    }

    try {
      const days = [
        "Sun",
        "Mon",
        "Tues",
        "Wed",
        "Thur",
        "Fri",
        "Sat",
      ];

      const available_days = days
        .map((day) => (hours[day]?.closed ? "0" : "1"))
        .join(",");

      const start_time = days
        .map((day) => {
          const d = hours[day] || {};
          if (d.closed) return "";
          return d.open && d.open !== "--:--" ? d.open : "";
        })
        .join(",");

      const end_time = days
        .map((day) => {
          const d = hours[day] || {};
          if (d.closed) return "";
          return d.close && d.close !== "--:--" ? d.close : "";
        })
        .join(",");

      const formData = {
        ...manualForm,
        country: "USA",
        available_days,
        start_time,
        end_time,
        id: user.id,
        access_token: user.access_token,
        vatm_fee: customerTotalFee,
        split: retailLocationFee,
      };

      const res = await fetch(`${api.main}api/agent/add_single_location`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error("Submission failed");

      await res.json();

      // reset local state
      setManualForm({});
      setHours(initialHours);
      setCustomerTotalFee("");
      setRetailLocationFee("");
      setShowTooltip(false);
      setShowErrorMessage(false);

      Modal.success({
        title: "Success!",
        content: "Location added successfully.",
        onOk: () => navigate("/view-locations"),
      });
    } catch (err) {
      Modal.error({
        title: "Error",
        content: err.message || "Something went wrong.",
      });
    }
  };

  const hasFieldError = (name) => missingFields.includes(name);
  const shouldShowInlineErrors =
    missingFields.length > 0 && missingFields.length <= 3;

  // --------- render ----------

  return (
    <div className="manual-upload-page">
      {toastMessage && (
        <div
          className="toast-notification"
          style={{
            position: "fixed",
            top: "80px",
            right: "24px",
            backgroundColor: colors.red,
            color: colors.white,
            padding: "10px 16px",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            zIndex: 2000,
            maxWidth: "320px",
            fontSize: "0.9rem",
          }}
        >
          {toastMessage}
        </div>
      )}

      <div className="page-header">
        <div>
          <p className="page-eyebrow">Locations</p>
          <h2>Manual Location Entry</h2>
          <p className="section-subtitle">
            Complete the form below to add a single location with full details.
          </p>
        </div>
      </div>

      <section className="page-section manual-upload-panel">
        <div className="manual-card">
          <form className="manual-upload-form" onSubmit={handleSubmit}>
        <h3>Business Information</h3>
        <div className="location-section">
          <div className="location-item">
            <label>Business Name*</label>
            <input
              className="location-input"
              type="text"
              name="business_name"
              value={manualForm.business_name || ""}
              onChange={handleInputChange}
              style={{
                borderColor: hasFieldError("business_name")
                  ? colors.red
                  : undefined,
              }}
            />
            {shouldShowInlineErrors && hasFieldError("business_name") && (
              <span style={{ color: colors.red, fontSize: "0.8rem" }}>
                This field is required.
              </span>
            )}
          </div>

          <div className="location-item">
            <label>Business Type</label>
            <CreatableSelect
              isClearable
              styles={{
                container: (base) => ({ ...base, margin: 0 }),
                control: (base) => ({
                  ...base,
                  borderRadius: "12px",
                  minHeight: "42px",
                  boxShadow: "none",
                  fontSize: "1rem",
                  fontWeight: "1000",
                  borderColor: "#cccccc",
                  margin: 0,
                }),
                input: (base) => ({ ...base, padding: "0px", margin: 0 }),
                valueContainer: (base) => ({
                  ...base,
                  padding: "0px 8px",
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  padding: "0px 4px",
                }),
              }}
              options={[
                { value: "Food - Restaurant", label: "Food - Restaurant" },
                { value: "Food - Deli / Bodega", label: "Food - Deli / Bodega" },
                {
                  value: "Shop & Service - Car Wash",
                  label: "Shop & Service - Car Wash",
                },
                {
                  value: "Shop & Service - Convenience",
                  label: "Shop & Service - Convenience",
                },
                {
                  value: "Shop & Service - Gas Station",
                  label: "Shop & Service - Gas Station",
                },
              ]}
              value={
                manualForm.business_type
                  ? {
                      value: manualForm.business_type,
                      label: manualForm.business_type,
                    }
                  : null
              }
              onChange={(selectedOption) =>
                handleInputChange({
                  target: {
                    name: "business_type",
                    value: selectedOption ? selectedOption.value : "",
                  },
                })
              }
            />
          </div>

          <div className="location-item">
            <label>Address*</label>
            <input
              className="location-input"
              type="text"
              name="business_address"
              placeholder="ex: 1234 Juneau Lane"
              value={manualForm.business_address || ""}
              onChange={handleInputChange}
              style={{
                borderColor: hasFieldError("business_address")
                  ? colors.red
                  : undefined,
              }}
            />
            {shouldShowInlineErrors && hasFieldError("business_address") && (
              <span style={{ color: colors.red, fontSize: "0.8rem" }}>
                This field is required.
              </span>
            )}
          </div>
        </div>

        <div className="location-section">
          <div className="location-item">
            <label>City*</label>
            <input
              className="location-input"
              type="text"
              name="city"
              value={manualForm.city || ""}
              onChange={handleInputChange}
              style={{
                borderColor: hasFieldError("city") ? colors.red : undefined,
              }}
            />
            {shouldShowInlineErrors && hasFieldError("city") && (
              <span style={{ color: colors.red, fontSize: "0.8rem" }}>
                This field is required.
              </span>
            )}
          </div>

          <div className="location-item">
            <label>State*</label>
            <input
              className="location-input"
              type="text"
              name="state"
              maxLength={2}
              placeholder="ex: CA"
              value={manualForm.state || ""}
              onChange={handleInputChange}
              style={{
                borderColor: hasFieldError("state") ? colors.red : undefined,
              }}
            />
            {shouldShowInlineErrors && hasFieldError("state") && (
              <span style={{ color: colors.red, fontSize: "0.8rem" }}>
                This field is required.
              </span>
            )}
          </div>

          <div className="location-item">
            <label>Zipcode*</label>
            <input
              className="location-input"
              type="text"
              name="zipcode"
              value={manualForm.zipcode || ""}
              onChange={handleInputChange}
              style={{
                borderColor: hasFieldError("zipcode") ? colors.red : undefined,
              }}
            />
            {shouldShowInlineErrors && hasFieldError("zipcode") && (
              <span style={{ color: colors.red, fontSize: "0.8rem" }}>
                This field is required.
              </span>
            )}
          </div>

          <div className="location-item" style={{ position: "relative" }}>
            <label>Daily Cash Limit ($)</label>
            <input
              className="location-input"
              type="text"
              name="daily_cash_limit"
              placeholder="ex: 100"
              value={manualForm.daily_cash_limit ?? ""}
              onChange={(e) => {
                const raw = e.target.value;

                // 1) Strip everything except digits and "."
                let cleaned = raw.replace(/[^0-9.]/g, "");

                // 2) Ensure only ONE decimal point
                const parts = cleaned.split(".");
                if (parts.length > 2) {
                  cleaned = parts[0] + "." + parts.slice(1).join("");
                }

                // 3) Normalize leading "." → "0.x"
                if (cleaned.startsWith(".")) {
                  cleaned = "0" + cleaned;
                }

                // 4) Enforce upper limit of 10000
                if (cleaned) {
                  const numericVal = parseFloat(cleaned);
                  if (!isNaN(numericVal) && numericVal > 10000) {
                    cleaned = "10000";
                  }
                }

                handleInputChange({
                  target: {
                    name: "daily_cash_limit",
                    value: cleaned,
                  },
                });
              }}
              style={{
                backgroundColor: "white",
              }}
            />
          </div>
        </div>

        {/* Fee section */}
        <div className="location-section">
          <div className="location-item">
            <label>Customer&apos;s Fee ($)</label>
            <input
              type="text"
              className="location-input"
              value={customerTotalFee}
              placeholder="0.00"
              onChange={(e) =>
                handleFeeInputChange("customerTotalFee", e.target.value)
              }
            />
          </div>
          <div className="location-item" style={{ position: "relative" }}>
            <label>Retail Location Fee ($)</label>
            <input
              type="text"
              className="location-input"
              value={retailLocationFee}
              placeholder="0.00"
              onChange={(e) => handleRetailLocationFeeChange(e.target.value)}
              onFocus={handleRetailLocationFocus}
              onBlur={handleRetailLocationBlur}
              disabled={
                !customerTotalFee || parseFloat(customerTotalFee) <= 0
              }
              style={{
                backgroundColor:
                  !customerTotalFee || parseFloat(customerTotalFee) <= 0
                    ? "#f5f5f5"
                    : "white",
                cursor:
                  !customerTotalFee || parseFloat(customerTotalFee) <= 0
                    ? "not-allowed"
                    : "text",
                borderColor: showErrorMessage ? colors.red : "",
              }}
            />
            {showTooltip && (
              <div
                style={{
                  position: "absolute",
                  top: "-35px",
                  left: "0",
                  backgroundColor: "#333",
                  color: "white",
                  padding: "5px 10px",
                  borderRadius: "5px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  zIndex: 1000,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              >
                Please input customer&apos;s fee first.
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: "20px",
                    width: "0",
                    height: "0",
                    borderLeft: "5px solid transparent",
                    borderRight: "5px solid transparent",
                    borderTop: "5px solid #333",
                  }}
                />
              </div>
            )}
            {showErrorMessage && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: "0",
                  backgroundColor: colors.red,
                  color: "white",
                  padding: "5px 10px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  marginTop: "2px",
                  zIndex: 1000,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              >
                Amount cannot exceed ${getMaxAllowedRetailFee().toFixed(2)}
                <div
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: "20px",
                    width: "0",
                    height: "0",
                    borderLeft: "5px solid transparent",
                    borderRight: "5px solid transparent",
                    borderBottom: `5px solid ${colors.red}`,
                  }}
                />
              </div>
            )}
          </div>
          <div className="location-item">
            <label>
              Spare&apos;s Fee ({vatmPercentage !== null ? vatmPercentage : "--"}
              %)
            </label>
            <div
              className="location-input"
              style={{ backgroundColor: "#f5f5f5" }}
            >
              ${calculateSparesFee().toFixed(2)}
            </div>
          </div>
          <div className="location-item">
            <label>Your Net Fee ($)</label>
            <div
              className="location-input"
              style={{ backgroundColor: "#f5f5f5" }}
            >
              ${calculateYourNetFee().toFixed(2)}
            </div>
          </div>
        </div>

        {/* Hours */}
        {hoursErrorMessage && (
          <div
            style={{
              marginTop: "0.5rem",
              marginBottom: "0.5rem",
              padding: "8px 12px",
              backgroundColor: colors.pink,
              color: colors.red,
              borderRadius: "6px",
              fontSize: "0.9rem",
            }}
          >
            {hoursErrorMessage}
          </div>
        )}

        <h3>Hours of Operation*</h3>

        {/* If you want the button back, just uncomment this */}
        {/* <div style={{ marginBottom: "8px" }}>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ borderRadius: 12, padding: "4px 10px", fontSize: "0.85rem" }}
            onClick={() => applyDayToAll("Monday")}
          >
            Copy Monday's hours to all days
          </button>
        </div> */}

        <div
          className="hours-section"
        >
          {daysOrder.map((day) => {
            const dayData =
              hours[day] || { open: "--:--", close: "--:--", closed: false };
            const hasError = hoursErrorDays.includes(day);

            const openVal =
              dayData.open && dayData.open !== "--:--" ? dayData.open : "";
            const closeVal =
              dayData.close && dayData.close !== "--:--" ? dayData.close : "";
            const openMinutes = parseTimeToMinutes(openVal);
            const closeMinutes = parseTimeToMinutes(closeVal);
            const invalidOrder =
              openMinutes !== null &&
              closeMinutes !== null &&
              openMinutes >= closeMinutes;
            const copyDisabled =
              !!dayData.closed || !openVal || !closeVal || invalidOrder;

            return (
              <div
                key={day}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "8px 10px",
                  borderRadius: "10px",
                  border: `1px solid ${
                    hasError && !dayData.closed ? colors.red : "#e0e0e0"
                  }`,
                  backgroundColor: "#fafafa",
                }}
              >
                {/* Day label + Closed toggle */}
                <label
                  style={{
                    width: "100%",
                    fontWeight: "bold",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    color: hasError ? colors.red : undefined,
                    marginBottom: 4,
                  }}
                >
                  <span>{day}</span>
                  <span style={{ fontSize: "0.8rem" }}>
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!dayData.closed}
                        onChange={(e) =>
                          handleHoursChange(day, "closed", e.target.checked)
                        }
                        style={{ accentColor: "var(--light-blue)" }}
                      />
                      Closed
                    </label>
                  </span>
                </label>

                {/* Stacked time inputs */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    marginTop: 4,
                  }}
                >
                  <input
                    type="time"
                    step="1800" // 30 min steps
                    value={openVal}
                    disabled={!!dayData.closed}
                    onChange={(e) =>
                      handleHoursChange(day, "open", e.target.value)
                    }
                    style={{
                      borderRadius: "8px",
                      padding: "4px 6px",
                      border: `1px solid ${
                        hasError && !dayData.closed ? colors.red : "#ccc"
                      }`,
                    }}
                  />
                  <input
                    type="time"
                    step="1800"
                    value={closeVal}
                    disabled={!!dayData.closed}
                    onChange={(e) =>
                      handleHoursChange(day, "close", e.target.value)
                    }
                    style={{
                      borderRadius: "8px",
                      padding: "4px 6px",
                      border: `1px solid ${
                        hasError && !dayData.closed ? colors.red : "#ccc"
                      }`,
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    marginTop: 4,
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={copyDisabled}
                    style={{
                      borderRadius: 5,
                      padding: "4px 10px",
                      fontSize: "0.85rem",
                      opacity: copyDisabled ? 0.6 : 1,
                      cursor: copyDisabled ? "not-allowed" : "pointer",
                    }}
                    onClick={() => applyDayToAll(day)}
                  >
                    Copy to all
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact Info */}
        <h3>Contact Information</h3>
        <div className="location-section">
          <div className="location-item">
            <label>Contact Name*</label>
            <input
              className="location-input"
              type="text"
              name="contact_name"
              value={manualForm.contact_name || ""}
              onChange={handleInputChange}
              style={{
                borderColor: hasFieldError("contact_name")
                  ? colors.red
                  : undefined,
              }}
            />
            {shouldShowInlineErrors && hasFieldError("contact_name") && (
              <span style={{ color: colors.red, fontSize: "0.8rem" }}>
                This field is required.
              </span>
            )}
          </div>
          <div className="location-item">
            <label>Contact Email*</label>
            <input
              className="location-input"
              type="email"
              name="contact_email"
              value={manualForm.contact_email || ""}
              onChange={handleInputChange}
              style={{
                borderColor: hasFieldError("contact_email")
                  ? colors.red
                  : undefined,
              }}
            />
            {shouldShowInlineErrors && hasFieldError("contact_email") && (
              <span style={{ color: colors.red, fontSize: "0.8rem" }}>
                This field is required.
              </span>
            )}
          </div>
          <div className="location-item">
            <label>Contact Phone*</label>
            <PhoneInput
              country={"us"}
              value={manualForm.contact_phone || ""}
              inputProps={{ name: "contact_phone" }}
              onChange={(value) =>
                handleInputChange({
                  target: {
                    name: "contact_phone",
                    value: value.startsWith("+") ? value : "+" + value,
                  },
                })
              }
              style={{
                borderColor: hasFieldError("contact_phone")
                  ? colors.red
                  : undefined,
              }}
              inputClass="custom-phone-input"
              buttonClass="custom-phone-button"
              containerClass="custom-phone-container"
            />
            {shouldShowInlineErrors && hasFieldError("contact_phone") && (
              <span style={{ color: colors.red, fontSize: "0.8rem" }}>
                This field is required.
              </span>
            )}
          </div>
        </div>

        {/* Banking details */}
        <h3>Banking Details</h3>
        <div className="location-section">
          <div className="location-item">
            <label>Bank Name</label>
            <input
              className="location-input"
              type="text"
              name="bank_name"
              value={manualForm.bank_name || ""}
              onChange={handleInputChange}
              style={{
                borderColor: hasFieldError("bank_name") ? colors.red : undefined,
              }}
            />
          </div>
          <div className="location-item">
            <label>Account Holder Name</label>
            <input
              className="location-input"
              type="text"
              name="account_holder_name"
              value={manualForm.account_holder_name || ""}
              onChange={handleInputChange}
              style={{
                borderColor: hasFieldError("account_holder_name")
                  ? colors.red
                  : undefined,
              }}
            />
          </div>
          <div className="location-item">
            <label>Account Number</label>
            <input
              className="location-input"
              type="text"
              name="account_number"
              value={manualForm.account_number || ""}
              onChange={handleInputChange}
              style={{
                borderColor: hasFieldError("account_number")
                  ? colors.red
                  : undefined,
              }}
            />
          </div>
          <div className="location-item">
            <label>Routing Number</label>
            <input
              className="location-input"
              type="text"
              name="routing_number"
              value={manualForm.routing_number || ""}
              onChange={handleInputChange}
              style={{
                borderColor: hasFieldError("routing_number")
                  ? colors.red
                  : undefined,
              }}
            />
          </div>
        </div>

        <div className="location-section location-section--actions">
          <button
            className="btn btn-primary"
            style={{ width: "200px" }}
            type="submit"
          >
            Add Location
          </button>
        </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ManualUpload;
